import { createUrl, getTenantInfo, sendRequest } from 'utils/utils'
import Infra from '../../mobx/Infra'

interface tenantUrls {
	storeAndMenuJsonURL: string
	webFlowAddressURL: string
	tenantDetailsURL: string
	menuUrl: string
}

interface storeUrls {
	storeAndMenuJsonURL: string
	storeDetailsUrl: string
	menuUrl: string
}

export enum TenantUrlPickOptions {
	WebFlowAddressURL = 'webFlowAddressURL',
	StoreAndMenuJsonURL = 'storeAndMenuJsonURL',
	TenantDetailsURL = 'tenantDetailsURL',
	MenuUrl = 'menuUrl',
}

const tenantUrlsCache: Record<string, Promise<tenantUrls>> = {}
const storeUrlsCache: Record<string, Promise<storeUrls>> = {}
let lastUrl: string | null = null

const resetCacheIfRouteChanged = () => {
	if (typeof window === 'undefined') {
		return
	}

	const currentUrl = window.location.href

	if (lastUrl !== currentUrl) {
		lastUrl = currentUrl
		for (const key of Object.keys(tenantUrlsCache)) {
			delete tenantUrlsCache[key]
		}
		for (const key of Object.keys(storeUrlsCache)) {
			delete storeUrlsCache[key]
		}
	}
}

/**
 * DVP-1403. These caches are module-level and resetCacheIfRouteChanged is browser-only, so a
 * cached rejection was permanent on the server: the URLs were never retried for the life of the
 * pod and the retained AxiosError held its config/response.
 */
const evictOnFailure =
	<T>(cache: Record<string, Promise<T>>, key: string) =>
	(error: unknown): never => {
		delete cache[key]
		throw error
	}

export const storeUrls = async (storeId: string): Promise<storeUrls> => {
	resetCacheIfRouteChanged()

	if (!storeUrlsCache[storeId]) {
		storeUrlsCache[storeId] = (
			sendRequest(
				true,
				createUrl(Infra?.appParams?.storefrontUrl || process.env.STOREFRONT_API_URL, `/v1/stores/${storeId}/urls`),
				'get',
				null,
				null,
				true,
				90000,
				null,
				false
			) as Promise<storeUrls>
		).catch(evictOnFailure(storeUrlsCache, storeId))
	}
	return storeUrlsCache[storeId]
}

export const tenantUrls = async (pickOptions: TenantUrlPickOptions[], tenantId?: string, showLoader = true): Promise<tenantUrls> => {
	resetCacheIfRouteChanged()

	const resolvedTenantId = tenantId || getTenantInfo()
	const pickKey = pickOptions.sort().join(',')
	const cacheKey = `${resolvedTenantId}|${pickKey}`

	if (!tenantUrlsCache[cacheKey]) {
		const pickQuery = pickOptions.join(',')
		tenantUrlsCache[cacheKey] = (
			sendRequest(
				showLoader,
				createUrl(
					Infra?.appParams?.storefrontUrl || process.env.STOREFRONT_API_URL,
					`/v1/tenants/${resolvedTenantId}/urls?$pick=${pickQuery}`
				),
				'get',
				null,
				null,
				true,
				90000,
				null,
				false
			) as Promise<tenantUrls>
		).catch(evictOnFailure(tenantUrlsCache, cacheKey))
	}

	return tenantUrlsCache[cacheKey]
}
